/* A real elliptical carousel: every item follows the same continuous orbit. */
(() => {
  if (customElements.get('woodlas-orbit')) return;
  class WoodlasOrbit extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.items = [...this.querySelectorAll('[data-orbit-item]')];
      this.panels = [...this.querySelectorAll('[data-orbit-panel]')];
      this.dots = [...this.querySelectorAll('[data-orbit-dot]')];
      this.stage = this.querySelector('[data-orbit-stage]');
      if (!this.stage || !this.items.length) return;
      this.position = this.target = this.active = 0;
      this.entranceProgress = 1; this.entranceStarted = false;
      this.duration = Number(this.dataset.duration) || 700;
      this.interval = Math.max(1000, Number(this.dataset.interval) || 3000);
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = this.reduced.matches;
      this.visible = false;
      this.focused = this.editing = false;
      this.lastInteraction = 0;
      const on = (el, event, handler, options = {}) => el?.addEventListener(event, handler, { ...options, signal: this.controller.signal });
      on(this.querySelector('[data-orbit-prev]'), 'click', () => this.move(-1));
      on(this.querySelector('[data-orbit-next]'), 'click', () => this.move(1));
      on(this.querySelector('[data-orbit-pause]'), 'click', () => {
        this.userPaused = !this.userPaused;
        this.updatePause();
        this.schedule();
      });
      this.dots.forEach((dot, i) => on(dot, 'click', () => this.select(i)));
      this.items.forEach((item, i) => on(item, 'click', (event) => {
        if (performance.now() - this.lastInteraction < 450) { event.preventDefault(); return; }
        this.select(i);
      }));
      on(this.stage, 'keydown', (event) => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault(); this.move(event.key === 'ArrowRight' ? 1 : -1);
        }
        if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault(); this.select(event.key === 'Home' ? 0 : this.items.length - 1);
        }
      });
      on(this.stage, 'pointerdown', this.pointerDown.bind(this));
      on(this.stage, 'pointermove', this.pointerMove.bind(this), { passive: false });
      on(this.stage, 'pointerup', this.pointerEnd.bind(this));
      on(this.stage, 'pointercancel', (event) => this.pointerEnd(event, true));
      on(this.stage, 'lostpointercapture', (event) => {
        // A touch initially captured by a product button transfers to the stage.
        // Ignore that child's bubbling event; it is not the end of this drag.
        if (event.target === this.stage) this.pointerEnd(event, true);
      });
      on(this.stage, 'dragstart', (event) => event.preventDefault());
      on(this, 'focusin', (event) => { this.focused = event.target.matches(':focus-visible'); this.schedule(); });
      on(this, 'focusout', (event) => { if (!this.contains(event.relatedTarget)) { this.focused = false; this.schedule(); } });
      on(document, 'visibilitychange', () => { if (document.hidden) this.finishEntrance(); this.schedule(); });
      on(this.reduced, 'change', () => {
        if (this.reduced.matches) { this.userPaused = true; this.animateTo(this.target, false); }
        this.updatePause(); this.schedule();
      });
      on(document, 'shopify:block:select', (event) => {
        const index = this.items.findIndex((item) => item.dataset.blockId === event.detail.blockId);
        if (index < 0) return;
        this.editing = true; this.select(index, false);
      });
      on(document, 'shopify:block:deselect', () => { this.editing = false; this.schedule(); });
      this.items.forEach((item) => on(item.querySelector('img'), 'load', () => this.measure()));
      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this.stage);
      this.visibilityObserver = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        if (this.visible) this.startEntrance();
        else if (this.entering) this.finishEntrance();
        this.schedule();
      }, { threshold: 0.15 });
      this.visibilityObserver.observe(this);
      this.classList.add('is-ready');
      this.measure(); this.updateActive(false); this.updatePause();
      on(this.items[0].querySelector('img'), 'load', () => { this.startEntrance(); this.schedule(); }, { once: true });
    }

    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      this.resizeObserver?.disconnect(); this.visibilityObserver?.disconnect();
      cancelAnimationFrame(this.frame); cancelAnimationFrame(this.entranceFrame); this.stopTimer();
    }

    // Main product and copy are already painted. Only smaller side products move.
    startEntrance() {
      const image = this.items[0].querySelector('img');
      if (this.entranceStarted || !this.visible || document.hidden || this.reduced.matches || this.dataset.entrance !== 'true' || (image && (!image.complete || !image.naturalWidth))) return;
      this.entranceStarted = this.entering = true;
      this.stopTimer();
      // Allow the hero's first paint before starting any decorative work.
      this.entranceFrame = requestAnimationFrame(() => {
        this.entranceFrame = requestAnimationFrame(() => {
          if (!this.isConnected || this.reduced.matches || !this.visible) { this.finishEntrance(); return; }
          this.classList.add('is-entering');
          const start = performance.now();
          const tick = (now) => {
            const t = Math.min(1, (now - start) / 1100);
            this.entranceProgress = 1 - (1 - t) ** 3;
            this.render();
            if (t < 1) this.entranceFrame = requestAnimationFrame(tick);
            else { this.finishEntrance(); this.schedule(); }
          };
          this.entranceFrame = requestAnimationFrame(tick);
        });
      });
    }

    finishEntrance() {
      cancelAnimationFrame(this.entranceFrame);
      this.entranceStarted = true;
      this.entering = false;
      this.entranceProgress = 1;
      this.classList.remove('is-entering');
      if (this.floorAnchors) this.render();
    }

    index(value) { return ((Math.round(value) % this.items.length) + this.items.length) % this.items.length; }

    measure() {
      const width = this.stage.clientWidth;
      this.mobile = width < 750;
      // The visible gold ellipse is the source of truth for the motion path.
      // Anchor each product at its feet, including padding in bundled cutouts.
      const ring = this.querySelector('.wl-orbit__ellipse');
      this.radius = ring.offsetWidth / 2;
      this.depth = ring.offsetHeight / 2;
      this.centerX = ring.offsetLeft + this.radius - width / 2;
      this.centerY = ring.offsetTop + this.depth;
      this.floorAnchors = this.items.map((item) => {
        const img = item.querySelector('img');
        const aspect = img?.naturalWidth && img?.naturalHeight ? img.naturalWidth / img.naturalHeight : 1;
        const imageHeight = Math.min(item.clientHeight, item.clientWidth / aspect);
        return {
          base: item.offsetTop + item.offsetHeight,
          inset: imageHeight * Number(item.dataset.floorInset || 0)
        };
      });
      this.render();
    }

    render() {
      const count = this.items.length;
      this.items.forEach((item, i) => {
        const entrance = i === 0 ? 0 : .18 * (1 - (this.entranceProgress ?? 1));
        const angle = (this.position - i) * Math.PI * 2 / count + entrance;
        const front = count === 1 ? 1 : (Math.cos(angle) + 1) / 2;
        const scale = 0.22 + Math.pow(front, 3.5) * 0.78;
        const x = this.centerX + (count === 1 ? 0 : Math.sin(angle) * this.radius);
        const anchor = this.floorAnchors[i];
        const y = this.centerY + Math.cos(angle) * this.depth - anchor.base + anchor.inset * scale;
        item.style.transform = `translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, 0) scale(${scale.toFixed(4)})`;
        item.style.zIndex = String(Math.round(front * 100) + 1);
        item.style.opacity = String(0.72 + front * 0.28);
      });
    }

    updateActive(announce) {
      this.active = this.index(this.target);
      this.items.forEach((item, i) => {
        item.tabIndex = i === this.active ? 0 : -1;
        item.setAttribute('aria-pressed', String(i === this.active));
      });
      this.panels.forEach((panel, i) => { panel.hidden = i !== this.active; });
      this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === this.active)));
      const counter = this.querySelector('[data-orbit-counter]');
      if (counter) counter.textContent = String(this.active + 1).padStart(2, '0');
      if (announce) this.querySelector('[data-orbit-status]').textContent = this.items[this.active].getAttribute('aria-label');
      this.dataset.activeIndex = String(this.active);
    }

    animateTo(target, announce = true, autoplay = false) {
      this.finishEntrance(); this.classList.add('has-interacted');
      this.stopTimer(); cancelAnimationFrame(this.frame);
      this.target = target;
      this.updateActive(announce);
      const start = this.position;
      const distance = target - start;
      if (this.reduced.matches || Math.abs(distance) < 0.001) {
        this.position = target; this.render(); this.animating = false; this.schedule(); return;
      }
      this.animating = true;
      const requestedDuration = this.duration * Math.min(1.5, Math.max(0.6, Math.abs(distance)));
      const duration = autoplay ? Math.min(requestedDuration, this.interval * .7) : requestedDuration;
      const started = performance.now();
      const tick = (now) => {
        const t = Math.min(1, (now - started) / duration);
        // Quintic easing gives an unbroken position when an arrow interrupts motion.
        const eased = t < 0.5 ? 16 * t ** 5 : 1 - ((-2 * t + 2) ** 5) / 2;
        this.position = start + distance * eased;
        this.render();
        if (t < 1) this.frame = requestAnimationFrame(tick);
        else { this.position = this.target = this.index(target); this.animating = false; this.render();
          // Autoplay interval is start-to-start, including the transition.
          this.schedule(autoplay ? Math.max(0, this.interval - (now - started)) : this.interval); }
      };
      this.frame = requestAnimationFrame(tick);
    }

    move(direction, announce = true, autoplay = false) { if (this.items.length > 1) this.animateTo(this.target + direction, announce, autoplay); }

    select(index, announce = true) {
      let delta = index - this.index(this.target);
      if (delta > this.items.length / 2) delta -= this.items.length;
      if (delta < -this.items.length / 2) delta += this.items.length;
      this.animateTo(this.target + delta, announce);
    }

    pointerDown(event) {
      if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0) || this.items.length < 2) return;
      this.focused = false;
      this.finishEntrance(); this.classList.add('has-interacted');
      this.gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, start: this.position, time: performance.now(), locked: false };
      this.stopTimer();
    }

    pointerMove(event) {
      const gesture = this.gesture;
      if (!gesture || gesture.id !== event.pointerId) return;
      const dx = event.clientX - gesture.x;
      const dy = event.clientY - gesture.y;
      if (!gesture.locked) {
        if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) { this.gesture = null; this.schedule(); return; }
        if (Math.abs(dx) < 10) return;
        gesture.locked = true;
        gesture.start = this.position;
        cancelAnimationFrame(this.frame); this.animating = false;
        // Capture only after a horizontal intention, preserving vertical page scrolling.
        this.stage.setPointerCapture(event.pointerId);
        this.classList.add('is-dragging');
      }
      if (event.cancelable) event.preventDefault();
      this.position = gesture.start + dx / Math.max(130, this.radius);
      this.render();
    }

    pointerEnd(event, cancelled = false) {
      const gesture = this.gesture;
      if (!gesture || gesture.id !== event.pointerId) return;
      this.gesture = null;
      this.classList.remove('is-dragging');
      if (this.stage.hasPointerCapture(event.pointerId)) this.stage.releasePointerCapture(event.pointerId);
      if (!gesture.locked) { this.schedule(); return; }
      this.lastInteraction = performance.now();
      const dx = event.clientX - gesture.x;
      const velocity = dx / Math.max(1, performance.now() - gesture.time);
      const deliberate = Math.abs(dx) > 40 || (Math.abs(dx) > 15 && Math.abs(velocity) > 0.35);
      const destination = Math.round(gesture.start) + (!cancelled && deliberate ? Math.sign(dx) : 0);
      this.animateTo(destination, !cancelled);
    }

    updatePause() {
      const button = this.querySelector('[data-orbit-pause]');
      if (!button) return;
      button.setAttribute('aria-label', this.userPaused ? 'Start automatic rotation' : 'Pause automatic rotation');
      button.setAttribute('aria-pressed', String(this.userPaused));
      button.dataset.paused = String(this.userPaused);
    }

    stopTimer() {
      clearTimeout(this.timer); this.progressAnimation?.cancel(); this.progressAnimation = null;
    }

    schedule(delay = this.interval) {
      this.stopTimer();
      const firstImage = this.items[0]?.querySelector('img');
      if (firstImage && !firstImage.complete) return;
      if (this.dataset.autoplay !== 'true' || this.items.length < 2 || this.userPaused || this.reduced.matches || this.focused || this.editing || !this.visible || document.hidden || this.animating || this.entering || this.gesture) return;
      const fill = this.querySelector('[data-orbit-progress]');
      this.progressAnimation = fill?.animate([{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }], { duration: delay, fill: 'forwards' });
      this.timer = setTimeout(() => this.move(1, false, true), delay);
    }
  }
  customElements.define('woodlas-orbit', WoodlasOrbit);
})();
